'use client';

/**
 * Cart icon + item-count badge + dropdown in the navbar (Agency-only).
 *
 * Reads the transient cart via `useCart`. The dropdown shows the current count
 * and a "Voir le panier" link to `/cart`, or an empty-state message. Reserved to
 * Agency (the only role with a Panier) — the parent navbar mounts it only for
 * `role === 'agency'`.
 */

import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Menu,
    Typography,
} from '@mui/material';
import { radius } from '@odys/design-system/tokens/radius';
import { useLocale, useTranslations } from 'next-intl';
import { Fragment, useEffect, useRef, useState } from 'react';

import { formatPrice } from '@/components/cart/cart-format';
import { Link } from '@/configurations/i18n/navigation';
import { useCart } from '@/configurations/providers/CartProvider';
import { trackNavigationClick } from '@/configurations/tracking/navigation';

export function CartMenuButton({ isOverlay = false }: { isOverlay?: boolean }) {
    const t = useTranslations('cart');
    const locale = useLocale();
    const { lines, count, isEmpty, lastAddSignal } = useCart();
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    // Auto-open the dropdown under the cart icon after a successful add (#1832).
    useEffect(() => {
        if (lastAddSignal > 0) setAnchorEl(buttonRef.current);
    }, [lastAddSignal]);

    return (
        <>
            <IconButton
                ref={buttonRef}
                onClick={(event) => {
                    trackNavigationClick({ navItem: 'cart', triggerContext: 'navbar' });
                    setAnchorEl(event.currentTarget);
                }}
                aria-label={t('open')}
                aria-haspopup="true"
                aria-expanded={open}
                data-testid="navbar-cart-button"
                sx={{ color: isOverlay ? 'common.white' : 'inherit' }}
            >
                <Badge badgeContent={count} color="primary" data-testid="navbar-cart-count">
                    <ShoppingCartRounded />
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 280, p: 1 } } }}
            >
                <Box sx={{ px: 1, py: 0.5 }}>
                    <Typography variant="subtitle2" component="p">
                        {t('title')}
                        {!isEmpty ? ` · ${count}` : ''}
                    </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                {isEmpty ? (
                    <Typography
                        variant="body2"
                        data-testid="navbar-cart-empty"
                        sx={{
                            color: 'text.secondary',
                            px: 1,
                            py: 0.5,
                        }}
                    >
                        {t('empty')}
                    </Typography>
                ) : (
                    <>
                        <List dense disablePadding sx={{ mb: 1 }} data-testid="navbar-cart-items">
                            {lines.map((line, index) => (
                                <Fragment key={line.lineId}>
                                    {index > 0 ? <Divider component="li" /> : null}
                                    <ListItem
                                        disableGutters
                                        sx={{
                                            px: 1,
                                            py: 0.75,
                                            gap: 1,
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        {line.imageUrl ? (
                                            <Box
                                                component="img"
                                                src={line.imageUrl}
                                                alt={line.productName}
                                                loading="lazy"
                                                sx={{
                                                    flexShrink: 0,
                                                    width: 48,
                                                    height: 36,
                                                    borderRadius: radius.sm,
                                                    objectFit: 'cover',
                                                    bgcolor: 'grey.100',
                                                }}
                                            />
                                        ) : null}
                                        <ListItemText
                                            primary={line.productName}
                                            secondary={
                                                [line.category, line.city]
                                                    .filter(Boolean)
                                                    .join(' · ') || undefined
                                            }
                                            slotProps={{
                                                primary: {
                                                    noWrap: true,
                                                    variant: 'body2',
                                                    title: line.productName,
                                                    sx: { fontWeight: 600 },
                                                },
                                                secondary: { noWrap: true, variant: 'caption' },
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, whiteSpace: 'nowrap', mt: 0.25 }}
                                        >
                                            {formatPrice(line.unitPrice, line.currency, locale)}
                                        </Typography>
                                    </ListItem>
                                </Fragment>
                            ))}
                        </List>
                        <Button
                            component={Link}
                            href="/cart"
                            variant="contained"
                            fullWidth
                            onClick={() => setAnchorEl(null)}
                            data-testid="navbar-cart-view"
                        >
                            {t('viewCart')}
                        </Button>
                    </>
                )}
            </Menu>
        </>
    );
}
